import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import short from 'short-uuid';
import { AppLink } from 'components/Link/AppLink';
import LoaderButton from 'components/Button/LoaderButton';
import InstructionsCheckbox from 'components/Input/InstructionsCheckboxInput';
import COLORS from 'constants/colors';
import NoticeMessage from '../../Messages/Notice';
import { ProviderAppShell } from '../../ProviderAppShell';
import { useAuthorizeContext } from '../../AuthorizeContext';
import { getManualContactName } from 'utils/connect-branding';
import { usePopOutInstructionsLink } from '../../../../store/pop-out-instructions';
import { Center } from '@chakra-ui/react';
import { ManualSignInInstructionProps } from '../DefaultSignInManualInstruction';
import { IsolvedInstructionsAugmentation } from './IsolvedInstructionsAugmentation';

const HELP_CENTER_URL =
  'https://support.tryfinch.com/articles/6586167653-isolved-add-an-authorized-user';

const ShellContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SmallTitle = styled.span`
  color: ${COLORS.FINCH.BLACK};
  font-size: 14px;
  line-height: 1.5;
  font-weight: 400;
`;

const InstructionsCard = styled.div`
  background: ${COLORS.WHITE};
  border: 1px solid ${COLORS.FINCH.LIGHT_GRAY};
  border-radius: 16px;
  overflow: hidden;
`;

const CardHeader = styled.div`
  border-bottom: 1px solid ${COLORS.FINCH.LIGHT_GRAY};
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 18px 20px 16px;
`;

const CardTitle = styled.h2`
  color: ${COLORS.FINCH.BLACK};
  font-size: 14px;
  font-weight: 500;
  line-height: 1.35;
  margin: 0;
`;

const StepsList = styled.div`
  display: flex;
  flex-direction: column;
`;

const StepRow = styled.div`
  border-top: 1px solid ${COLORS.FINCH.LIGHT_GRAY};
  display: grid;
  gap: 14px;
  grid-template-columns: 28px minmax(0, 1fr);
  padding: 18px 20px;

  &:first-child {
    border-top: none;
  }
`;

const StepBadge = styled.div`
  align-items: center;
  background: ${COLORS.GRAY.GRAY_100};
  border: 1px solid ${COLORS.FINCH.LIGHT_GRAY};
  border-radius: 999px;
  color: ${COLORS.FINCH.BLACK};
  display: flex;
  font-size: 13px;
  font-weight: 600;
  height: 28px;
  justify-content: center;
  line-height: 1;
  width: 28px;
`;

const StepBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
`;

const StepText = styled.p`
  color: ${COLORS.FINCH.BLACK};
  font-size: 14px;
  line-height: 1.6;
  margin: 0;
`;

const StepTextBold = styled.span`
  font-weight: 600;
`;

export const IsolvedManualSignInInstructions = ({
  onSubmit,
  onPrevStep,
  contactEmailInContext,
  companyNameInContext,
}: ManualSignInInstructionProps) => {
  const {
    client,
    clientName,
    payrollProvider,
    products,
    providerImplementation,
    error,
    setError,
  } = useAuthorizeContext() || {};
  const setPopOutInstructionsLink = usePopOutInstructionsLink(
    (state) => state.setPopOutInstructionsLink,
  );

  useEffect(() => {
    setPopOutInstructionsLink(HELP_CENTER_URL, null, {
      component: IsolvedInstructionsAugmentation,
    });

    return () => {
      setPopOutInstructionsLink(null, null);
    };
  }, [setPopOutInstructionsLink]);

  const shortIdRef = useRef<string>();
  const [didConfirmInstructions, setDidConfirmInstructions] = useState(false);

  if (!shortIdRef.current) {
    shortIdRef.current = short.generate();
  }

  const randomShortId = shortIdRef.current;

  if (!payrollProvider) throw new Error('no payroll provider');
  if (!providerImplementation) throw new Error('no provider implementation');
  if (!setError) throw new Error('no setError');

  const originalEmail =
    payrollProvider?.sessionAccountantEmail ||
    client?.manualConfig?.email ||
    payrollProvider?.defaultAccountantEmail ||
    '';
  let email = originalEmail;

  if (email.includes('@')) {
    const [handler, domain] = email.split('@');
    email = `${handler}+${randomShortId}@${domain}`;
  }

  const scopes = [];
  const org = ['company', 'directory', 'individual', 'employment'];
  const payroll = ['payment', 'pay_statement'];
  const benefits = ['deduction', 'benefits'];

  if (org.some((product) => products?.includes(product))) {
    scopes.push('Human Resources (HR)');
  }

  if (payroll.some((product) => products?.includes(product))) {
    scopes.push('Payroll');
  }

  if (benefits.some((product) => products?.includes(product))) {
    scopes.push('Benefits');
  }

  let scopeText = '';
  if (scopes.length > 1) {
    scopeText = `${scopes.slice(0, -1).join(', ')} and ${scopes.slice(-1)}`;
  } else {
    scopeText = scopes[0];
  }

  return (
    <ProviderAppShell
      provider={payrollProvider}
      error={error || null}
      providerImplementation={providerImplementation}
      setError={setError}
      goBack={
        !(companyNameInContext && contactEmailInContext)
          ? onPrevStep
          : undefined
      }
    >
      <>
        <ShellContent>
          <Center>
            <SmallTitle>
              Add Finch as a Client User to your payroll system
            </SmallTitle>
          </Center>

          <NoticeMessage>
            Complete the steps below, then click Connect
          </NoticeMessage>

          <InstructionsCard>
            <CardHeader>
              <CardTitle>Adding a Client User Overview</CardTitle>
            </CardHeader>

            <StepsList>
              <StepRow>
                <StepBadge>1</StepBadge>
                <StepBody>
                  <StepText>
                    Open the{' '}
                    <StepTextBold>
                      Client Contact Authorization Form
                    </StepTextBold>{' '}
                    by clicking the{' '}
                    <AppLink href={HELP_CENTER_URL} showInFrame>
                      link here
                    </AppLink>
                    .
                  </StepText>
                </StepBody>
              </StepRow>

              <StepRow>
                <StepBadge>2</StepBadge>
                <StepBody>
                  <StepText>
                    For the <StepTextBold>New Contact Information</StepTextBold>{' '}
                    section, use the following information:
                  </StepText>
                  <StepText>
                    First name:{' '}
                    <StepTextBold>
                      {getManualContactName(client, clientName)}
                    </StepTextBold>
                  </StepText>
                  <StepText>
                    Last name: <StepTextBold>Admin</StepTextBold>
                  </StepText>
                  <StepText>
                    Title: <StepTextBold>Admin</StepTextBold>
                  </StepText>
                  <StepText>
                    Primary: <StepTextBold>917-717-3974</StepTextBold>
                  </StepText>
                  <StepText>
                    Email address: <StepTextBold>{email}</StepTextBold>
                  </StepText>
                </StepBody>
              </StepRow>

              <StepRow>
                <StepBadge>3</StepBadge>
                <StepBody>
                  <StepText>
                    Select the <StepTextBold>{scopeText}</StepTextBold>{' '}
                    {scopes.length > 1 ? 'roles' : 'role'}
                  </StepText>
                </StepBody>
              </StepRow>

              <StepRow>
                <StepBadge>4</StepBadge>
                <StepBody>
                  <StepText>
                    Email the PDF to your {payrollProvider.displayName} rep and
                    CC: <StepTextBold>{originalEmail}</StepTextBold>
                  </StepText>
                </StepBody>
              </StepRow>
            </StepsList>
          </InstructionsCard>
        </ShellContent>
        <InstructionsCheckbox
          checked={didConfirmInstructions}
          onChange={(checked) => setDidConfirmInstructions(checked)}
          label="I confirm that I followed the instructions and sent the form"
        />
        <LoaderButton
          type="button"
          disabled={!didConfirmInstructions}
          text="Connect"
          onClick={() => {
            void onSubmit();
          }}
        />
      </>
    </ProviderAppShell>
  );
};
